<script lang="ts">
	import { PUBLIC_API_URL } from '$app/env/public';
	import type { Slider } from '#lib/schema-extension.js';
	import { setAttr } from '@directus/visual-editing';
	import { Carousel, Controls, Thumbnails } from 'flowbite-svelte';
	import { asObjectOrUndefined, getId } from '../directus.js';
	import { onMount } from 'svelte';

	let { block }: { block: Slider } = $props();

	const images = $derived.by(() => {
		return (
			block.images?.map((image, index) => {
				const file = asObjectOrUndefined(image)?.directus_files_id;
				const metadata = typeof file === 'object' && file !== null ? file : undefined;
				return {
					src: file ? `${PUBLIC_API_URL}/assets/${getId(file)}?key=medium` : '',
					alt:
						metadata?.description ||
						metadata?.title ||
						`${block.title || 'Galerie'} – Bild ${index + 1}`
				};
			}) ?? []
		);
	});

	let index = $state(0);
	let paused = $state(false);
	let reducedMotion = $state(true);
	onMount(() => {
		const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
		const update = () => (reducedMotion = preference.matches);
		update();
		preference.addEventListener('change', update);
		return () => preference.removeEventListener('change', update);
	});

	let height = $derived((block.height ?? 0) > 0 ? block.height : 300);

	let style: 'contain' | 'cover' = $derived(block.style?.includes('cover') ? 'cover' : 'contain');
</script>

<div
	class="bg-base-100 w-full p-3"
	data-directus={setAttr({
		collection: 'slider',
		item: block.id!,
		mode: 'modal'
	})}
>
	<h2
		class="mb-3"
		data-directus={setAttr({
			collection: 'slider',
			item: block.id!,
			fields: 'title',
			mode: 'popover'
		})}
	>
		{block.title}
	</h2>
	{#if images.length > 0}
		<Carousel
			{images}
			duration={block.autoplay && !paused && !reducedMotion && images.length > 1
				? Math.max(5000, block.autoplayDuration ?? 5000)
				: 0}
			role="region"
			aria-roledescription="Karussell"
			aria-label={block.title || 'Bildergalerie'}
			slideDuration={reducedMotion ? 0 : 500}
			isPreload
			slideFit={style ?? 'contain'}
			class="mx-auto rounded-none"
			style={`height: min(${height}px, 65vh) !important`}
			bind:index
		>
			<Controls>
				{#snippet children(changeSlide)}
					<button
						type="button"
						aria-label="Vorheriges Bild"
						class="btn btn-circle absolute top-1/2 left-3 z-30 -translate-y-1/2 border-0 bg-black/65 text-2xl text-white hover:bg-black/85"
						onclick={() => changeSlide(false)}>‹</button
					>
					<button
						type="button"
						aria-label="Nächstes Bild"
						class="btn btn-circle absolute top-1/2 right-3 z-30 -translate-y-1/2 border-0 bg-black/65 text-2xl text-white hover:bg-black/85"
						onclick={() => changeSlide(true)}>›</button
					>
				{/snippet}
			</Controls>
		</Carousel>
		<Thumbnails
			{images}
			ariaLabel="Bild anzeigen"
			class="mt-4 gap-3 overflow-x-auto bg-transparent pb-2 [&>button]:w-20 [&>button]:shrink-0"
			bind:index
		/>
		{#if block.autoplay && !reducedMotion && images.length > 1}
			<button
				type="button"
				class="btn btn-ghost mt-3"
				aria-pressed={paused}
				onclick={() => (paused = !paused)}
				>{paused ? 'Diashow fortsetzen' : 'Diashow pausieren'}</button
			>
		{/if}
	{/if}
</div>
